"use client"

import React, { useState } from 'react'
import { EngagementStatusBadge } from "@/components/ui/engagement-status-badge"
import { VersionSelector } from "@/components/engagement/VersionSelector"
import { EngagementActions } from "@/components/engagement/EngagementActions"
import { EngagementViewToggle } from "@/components/engagement/EngagementViewToggle"
import { useEngagementNavigation } from "@/contexts/engagement-navigation-context"
import { ENGAGEMENT_TABS, type NavigationMode } from "@/types/ui/engagement-view"
import type { FormState } from "@/types/ui/engagement"
import type { EngagementResponse } from "@/types/api/engagement"
import { useLanguage } from "@/contexts/language-context"
import type { ActionConfig } from "@/components/engagement/EngagementActions"
import { HistoryModal } from "./HistoryModal"

export interface EngagementToolbarProps {
  // Engagement state
  formState: FormState | null
  navigationMode: NavigationMode
  version: number
  recordId: number // For audit history grouping ID
  // Engagement display data
  engagementName?: string
  /** Live form value, so the pill tracks the checkbox before the engagement is saved. */
  isPreEngagement?: boolean
  allVersions: EngagementResponse[] | null
  allSectionsComplete: boolean
  // History contains sensitive data (e.g. pay rates). Hidden when the user cannot view pay rates.
  // TODO: Remove this gate once the History view surgically redacts sensitive fields.
  canViewPayRates: boolean

  // Action handlers
  isHandlingAction: boolean
  currentAction?: string
  actions: {
    refresh: ActionConfig
    save: ActionConfig
    submit: ActionConfig
    submitForReview: ActionConfig
    discard: ActionConfig
    approve: ActionConfig
    decline: ActionConfig
    createChangeRequest: ActionConfig
    recall: ActionConfig
    recallToAccounting: ActionConfig
    complete: ActionConfig
    reactivate: ActionConfig
  }
  
  // Unsaved changes
  confirmNavigation?: (onConfirm: () => void) => boolean
}

export function EngagementToolbar({
  formState,
  navigationMode,
  version,
  recordId,
  engagementName,
  isPreEngagement = false,
  allVersions,
  allSectionsComplete,
  canViewPayRates,
  isHandlingAction,
  currentAction,
  actions,
  confirmNavigation
}: EngagementToolbarProps) {
  const { dictionary } = useLanguage()
  const { activeTab, goToTab } = useEngagementNavigation()
  const [showHistoryModal, setShowHistoryModal] = useState(false)

  const engagementTitle = engagementName || "New Engagement"

  // Prepare actions component to avoid duplication
  const actionsElement = (
    <EngagementActions
      isHandlingAction={isHandlingAction}
      currentAction={currentAction}
      allSectionsComplete={allSectionsComplete}
      navigationMode={navigationMode}
      recordId={recordId}
      onShowHistory={canViewPayRates ? () => setShowHistoryModal(true) : undefined}
      actions={actions}
    />
  )

  return (
    <div className="bg-gray-100 rounded-lg border px-6 py-3">
      {/* 
        Grid Layout: 3 columns [1fr_auto_auto]
        - Column 1 (1fr): Flexible - grows to fill remaining space, pushing columns 2 & 3 to the right
        - Column 2 (auto): Sized to content - collapses to 0 when empty
        - Column 3 (auto): Sized to content - collapses to 0 when empty
        
        Two toolbar layouts controlled by navigationMode. GW-330: the status pill sits in the same
        slot in every state (immediately after the version selector, ahead of the action buttons),
        so it never moves as the engagement transitions Draft -> Pending Review -> Active -> edit.
        1. Setup Mode:
           [Col 1: Title | Version | Status ..............] [Col 2: Cancel | Trash | Save | Submit] [Col 3: empty]

        2. Existing Mode (readonly or edit):
           [Col 1: Title | Version | Status | Actions ....] [Col 2: empty] [Col 3: Overview | Details]
      */}
      <div className="grid grid-cols-[1fr_auto_auto] items-center gap-3">
        {/* Column 1: Title + Version + Status (+ Actions in existing mode) */}
        <div className="flex items-center gap-3">
          <h2 className="text-xl font-semibold text-slate-900">
            {engagementTitle}
          </h2>

          {/* Version selector - only show in details view */}
          {activeTab === ENGAGEMENT_TABS.DETAILS && (
            <VersionSelector
              allVersions={allVersions}
              currentVersion={version}
              confirmNavigation={confirmNavigation}
            />
          )}

          {/* Pre-engagement + Status pills - one fixed location for every mode and status */}
          {isPreEngagement && (
            <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-900">
              {dictionary?.engagement?.core?.isPreEngagement || "Pre-engagement"}
            </span>
          )}
          {formState?.actionContext.engagement.status && (
            <EngagementStatusBadge
              status={formState.actionContext.engagement.status}
            />
          )}

          {/* Action buttons - in existing mode, they stay on the left */}
          {navigationMode === "existing" && actionsElement}
        </div>

        {/* Column 2: Actions (setup mode) */}
        <div className="flex items-center gap-3">
          {/* Action buttons - in setup mode, they go to the right */}
          {navigationMode === "setup" && actionsElement}
        </div>

        {/* Column 3: View Toggle (only for existing engagements) */}
        <div>
          {navigationMode === "existing" && (
            <EngagementViewToggle
              activeTab={activeTab}
              onTabChange={goToTab}
            />
          )}
        </div>
      </div>

      {/* History Modal */}
      {recordId && canViewPayRates && (
        <HistoryModal
          recordId={recordId}
          engagementName={engagementName || "New Engagement"}
          currentVersion={version}
          allVersions={allVersions}
          open={showHistoryModal}
          onOpenChange={setShowHistoryModal}
        />
      )}
    </div>
  )
}
